// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Generic horizontal step progress bar used by every multi-step wizard in the
 * advisor app (relationship creation, Change of Agency, future wizards).
 *
 * <p>Decoupled from any specific wizard context: callers pass the steps list,
 * the current step ID, and (optionally) a per-step "completed" predicate. The
 * relationship wizard's previous {@code WizardProgress} component is now a
 * thin adapter around this one.
 */
export interface StepProgressBarStep<TId extends number | string> {
  /** Unique step identifier — also used as the visible badge number when no `label` is set */
  id: TId;
  /** Display name shown beneath the step circle */
  name: string;
}

export type StepProgressBarProps<TId extends number | string> = Readonly<{
  steps: ReadonlyArray<StepProgressBarStep<TId>>;
  currentStep: TId;
  /**
   * Returns true when the given step is "done" (typically: visited and
   * validated). Defaults to "every step before currentStep is done".
   */
  isStepCompleted?: (step: StepProgressBarStep<TId>, index: number) => boolean;
}>;

/**
 * Renders the same horizontal stepper UI used across all advisor wizards.
 * No state of its own — pure presentation.
 */
export function StepProgressBar<TId extends number | string>({
  steps,
  currentStep,
  isStepCompleted,
}: StepProgressBarProps<TId>) {
  const currentIndex = steps.findIndex((s) => s.id === currentStep);

  return (
    <div className="flex w-full items-center justify-center bg-white h-[80px] mb-[15px]">
      {steps.map((step, index) => {
        const isActive = step.id === currentStep;
        const completedByDefault = currentIndex >= 0 && index < currentIndex;
        const isDone = isStepCompleted
          ? isStepCompleted(step, index)
          : completedByDefault;
        const showConnector = index < steps.length - 1;
        let circleColorClass = 'bg-lilac-04 text-purple-primary';
        if (isDone) circleColorClass = 'bg-[#00A63E] !text-white';
        else if (isActive) circleColorClass = 'bg-purple-primary text-white';

        return (
          <div key={String(step.id)} className="flex items-center">
            {/* Step circle + label */}
            <div className="relative">
              <div
                className={`flex w-6 h-6 flex-col justify-center items-center rounded-[100px] ${circleColorClass} text-xs font-normal leading-normal font-sans`}
              >
                {isDone ? (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#step-progress-bar-check)">
                      <path d="M2.91699 7.0026L5.83366 9.91927L11.667 4.08594" stroke="white" strokeWidth="0.875" strokeLinecap="round" strokeLinejoin="round"/>
                    </g>
                    <defs>
                      <clipPath id="step-progress-bar-check">
                        <rect width="14" height="14" fill="white"/>
                      </clipPath>
                    </defs>
                  </svg>
                ) : (
                  index + 1
                )}
              </div>
              <span
                className={`absolute top-full mt-2 left-1/2 -translate-x-1/2 whitespace-nowrap text-body-03 font-medium leading-normal capitalize font-sans ${
                  isActive ? 'text-purple-primary' : 'text-grey-04'
                }`}
              >
                {step.name}
              </span>
            </div>

            {/* Connector line */}
            {showConnector && (
              <div
                className={`h-[1px] w-[112.5px] ${
                  isDone ? 'bg-[#00A63E]' : 'bg-lilac-04'
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default StepProgressBar;
